// src/components/tabs/PurchaseTab.js
import React from 'react';
import { formatPGT } from '../../utils/blockchain';
import { GAS_FEE_PER_TX } from '../../config/constants';

const PGT_ADDRESS = process.env.REACT_APP_PGT_ADDRESS;

// Fallback images from public/events/
const FALLBACK_IMAGES = ['/events/pregame.png', '/events/basefest.png', '/events/test.png'];
const getDefaultImage = (eventId) => {
  const hash = String(eventId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return FALLBACK_IMAGES[hash % FALLBACK_IMAGES.length];
};

export default function PurchaseTab({
  event,
  isBalanceZero,
  hasEnoughBalance,
  ticketTypes,
  cart,
  updateCart,
  hasItems,
  totalCost,
  // Purchase handlers
  handlePurchase,
  purchasing,
  purchaseError,
  purchaseSuccess,
  handleSinglePurchase,
  handleDevDirectPurchase,
  singleLoading,
  singleError,
  singleSuccess,
  devPurchaseLoading,
  IS_DEV_MODE,
  // Token buy
  supportedTokens,
  buyUSD,
  setBuyUSD,
  buyPGTWithToken,
  buyingPGT,
  // Approval
  approvePGT,
  approving,
  allowanceEnoughForCart,
  requiredForCartBN,
  // Ticket expansion
  expandedTicketTypes,
  toggleTicketTypeExpand,
  getTicketTypeInfo,
}) {
  return (
    <div role="tabpanel" id="panel-purchase" aria-labelledby="tab-purchase">
      {/* Event Info Controls */}
      <div className="event-controls">
        <div className="event-info-summary">
          <img
            src={event.image || getDefaultImage(event.id)}
            alt={event.name}
            className="event-thumb"
            onError={(e) => {
              const fallback = getDefaultImage(event.id);
              if (!e.target.src.endsWith(fallback)) {
                e.target.src = fallback;
              }
            }}
          />
          <div className="event-info-text">
            <h3>{event.name}</h3>
            <div className="event-meta">
              {event.date && (
                <span className="meta-item">
                  <span className="meta-icon">📅</span>
                  {new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {event.venue && (
                <span className="meta-item">
                  <span className="meta-icon">📍</span>
                  {event.venue}
                </span>
              )}
              {event.capacity && (
                <span className="meta-item">
                  <span className="meta-icon">👥</span>
                  {event.capacity.toLocaleString()} capacity
                </span>
              )}
            </div>
            {event.description && (
              <p className="event-description-short">{event.description}</p>
            )}
          </div>
        </div>
      </div>

      {isBalanceZero ? (
        <div className="buy-pgt-card">
          <h3>Buy PGT Tokens</h3>
          <p>1 PGT = $1. Choose your payment method:</p>
          <div className="usd-input">
            <label>Amount (USD / PGT): </label>
            <input
              type="number"
              min="1"
              step="1"
              value={buyUSD}
              onChange={(e) => setBuyUSD(Math.max(1, Number(e.target.value || 0)))}
            />
          </div>
          {supportedTokens.length > 0 ? (
            <div className="token-options">
              {supportedTokens.map((token) => (
                <button
                  key={token.symbol}
                  onClick={() => buyPGTWithToken(token)}
                  disabled={buyingPGT}
                  className="token-btn"
                  title={`Pay with ${token.symbol}`}
                >
                  Buy {buyUSD} PGT with {token.symbol}
                </button>
              ))}
            </div>
          ) : (
            <p>Loading payment options...</p>
          )}
          <p className="or-divider">— or —</p>
          <a
            href={`https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=${PGT_ADDRESS}&chain=${
              event.chainId === 8453 ? 'base' : 'base-sepolia'
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="dex-btn"
          >
            Buy on Uniswap
          </a>
        </div>
      ) : (
        <div className="ticket-cart">
          <h2>Select Tickets</h2>
          {ticketTypes.length === 0 ? (
            <p className="no-tickets">No tickets available for sale.</p>
          ) : (
            ticketTypes.map((type) => {
              const qty = cart[type.id] || 0;
              const sLoading = !!singleLoading[type.id];
              const sError = singleError[type.id];
              const sOk = singleSuccess[type.id];
              const isExpanded = expandedTicketTypes[type.id];
              const ticketTypeInfo = getTicketTypeInfo(type.id);
              const available = ticketTypeInfo?.maxSupply
                ? Math.max(0, Number(ticketTypeInfo.maxSupply) - Number(type.totalSupply || 0))
                : 999; // No cap if unknown
              const maxQty = Math.min(available, 10); // Cap at 10 per purchase
              const soldOut = available === 0;

              return (
                <div key={type.id} className={`cart-item ${isExpanded ? 'expanded' : ''}`}>
                  <div className="cart-item-main">
                    <div className="cart-item-info">
                      <div className="ticket-header-row">
                        <strong>{ticketTypeInfo?.name || type.metadataURI}</strong>
                        <button
                          className="expand-btn"
                          onClick={() => toggleTicketTypeExpand(type.id)}
                          title={isExpanded ? 'Hide details' : 'Show details'}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </div>
                      <p className="ticket-price">{formatPGT(type.price)} PGT each</p>
                      {soldOut && <p className="sale-inactive">Sold out</p>}
                      {!soldOut && ticketTypeInfo?.maxSupply && (
                        <p className="tickets-remaining">{available} remaining</p>
                      )}
                      {!type.isActive && !soldOut && (
                        <p className="sale-inactive">
                          {type.saleStart > Math.floor(Date.now() / 1000) ? 'Sale starts soon' : 'Sale ended'}
                        </p>
                      )}
                    </div>
                    <div className="amount-selector">
                      <button onClick={() => updateCart(type.id, Math.max(0, qty - 1))} disabled={!type.isActive || soldOut || !qty}>–</button>
                      <span>{qty}</span>
                      <button onClick={() => updateCart(type.id, qty + 1)} disabled={!type.isActive || soldOut || qty >= maxQty}>+</button>
                    </div>
                    <div className="single-buy">
                      <button
                        className="buy-one-btn"
                        disabled={!type.isActive || soldOut || sLoading}
                        onClick={() => handleSinglePurchase(type.id, 1)}
                        title={soldOut ? 'Sold out' : 'Buy 1 ticket of this type (gasless, EIP-712)'}
                      >
                        {sLoading ? 'Buying…' : soldOut ? 'Sold out' : 'Buy 1'}
                      </button>
                      {IS_DEV_MODE && (
                        <button
                          className="dev-purchase-btn"
                          disabled={!type.isActive || devPurchaseLoading[type.id]}
                          onClick={() => handleDevDirectPurchase(type.id, 1)}
                          title="[DEV] Direct purchase - bypasses QR scanning for testing"
                        >
                          {devPurchaseLoading[type.id] ? '⏳' : '🔧'}
                        </button>
                      )}
                      {sError && <div className="el-error small">❌ {sError}</div>}
                      {sOk && <div className="el-success small">✅ Done</div>}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="cart-item-details">
                      {ticketTypeInfo?.description && (
                        <p className="ticket-description">{ticketTypeInfo.description}</p>
                      )}
                      <div className="ticket-event-info">
                        {event.date && (
                          <div className="info-row">
                            <span className="info-label">Date:</span>
                            <span className="info-value">{new Date(event.date).toLocaleString()}</span>
                          </div>
                        )}
                        {event.venue && (
                          <div className="info-row">
                            <span className="info-label">Venue:</span>
                            <span className="info-value">{event.venue}</span>
                          </div>
                        )}
                        {event.capacity && (
                          <div className="info-row">
                            <span className="info-label">Capacity:</span>
                            <span className="info-value">{event.capacity.toLocaleString()} people</span>
                          </div>
                        )}
                        {ticketTypeInfo?.maxSupply && (
                          <div className="info-row">
                            <span className="info-label">Available:</span>
                            <span className="info-value">
                              {type.totalSupply !== undefined
                                ? `${Number(ticketTypeInfo.maxSupply) - Number(type.totalSupply)} / ${ticketTypeInfo.maxSupply}`
                                : ticketTypeInfo.maxSupply}
                            </span>
                          </div>
                        )}
                      </div>
                      {type.perks && type.perks.length > 0 && (
                        <div className="ticket-perks">
                          <span className="perks-label">Included Perks:</span>
                          <ul className="perks-list">
                            {type.perks.map((perk, idx) => (
                              <li key={idx}>{perk.name || perk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {hasItems && (
            <div className="cart-summary">
              <p>Total: {formatPGT(totalCost)} PGT <small>(incl. {formatPGT(GAS_FEE_PER_TX)} PGT gas)</small></p>
              {!allowanceEnoughForCart && (
                <div className="approve-block">
                  <p className="insufficient-funds">
                    Insufficient allowance for EventTicket. Minimum required:{' '}
                    <strong>{formatPGT(Number(requiredForCartBN.toString()))} PGT</strong>.
                  </p>
                  <button
                    className="approve-button"
                    onClick={() => approvePGT(requiredForCartBN)}
                    disabled={approving}
                    title="Approve EventTicket contract to spend your PGT"
                  >
                    {approving ? 'Approving…' : 'Approve PGT'}
                  </button>
                </div>
              )}
              {!hasEnoughBalance && <p className="insufficient-funds">Insufficient PGT balance</p>}
              {purchaseError && <p className="error-message">{purchaseError}</p>}
              {purchaseSuccess && <p className="success-message">✅ Purchase successful!</p>}
              <button
                className="buy-button"
                onClick={handlePurchase}
                disabled={purchasing || !hasEnoughBalance || !allowanceEnoughForCart}
              >
                {purchasing ? <span className="loader"></span> : 'Buy Selected Tickets'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
